import { forwardRef, useEffect, useId, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/cn';

export interface SelectOption<T extends string | number = string> {
  value: T;
  label: string;
}

export interface SelectProps<T extends string | number = string> {
  value?: T;
  options: SelectOption<T>[];
  placeholder?: string;
  invalid?: boolean;
  disabled?: boolean;
  id?: string;
  name?: string;
  required?: boolean;
  className?: string;
  /** Accessible name when the trigger is not wrapped by a <label htmlFor>. */
  'aria-label'?: string;
  'aria-labelledby'?: string;
  onChange?: (event: { target: { value: string } }) => void;
}

function optionId(selectId: string, label: string): string {
  return `${selectId}--${label.replace(/\s+/g, '_')}`;
}

function optionDomId(
  selectId: string | undefined,
  listId: string,
  option: { value: string | number; label: string },
): string {
  if (selectId) return optionId(selectId, option.label);
  return `${listId}--${String(option.value)}`;
}

function emitChange(
  onChange: SelectProps['onChange'] | undefined,
  value: string,
) {
  onChange?.({ target: { value } });
}

/**
 * Button listbox styled like TailAdmin inputs. Native `<option>` nodes do not
 * fire React `onChange` when automation taps them, so the listbox is made of
 * buttons. When `id` is set, each option gets `{id}--{label}` (whitespace becomes `_`) for
 * hops such as `admin.devices.terminal--No_terminal`.
 */
export const Select = forwardRef(function Select<
  T extends string | number = string,
>(
  {
    value,
    options,
    placeholder,
    invalid,
    className,
    disabled,
    id,
    onChange,
    name,
    required,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
  }: SelectProps<T>,
  ref: React.Ref<HTMLButtonElement>,
) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const reactId = useId();
  const listId = `${id ?? reactId}-listbox`;
  const selected = options.find((opt) => String(opt.value) === String(value));
  const display = selected?.label ?? placeholder ?? '';

  const focusOption = (index: number) => {
    const opt = options[index];
    if (!opt) return;
    document.getElementById(optionDomId(id, listId, opt))?.focus();
  };

  useEffect(() => {
    if (!open) return;
    const selectedIndex = options.findIndex((opt) => String(opt.value) === String(value));
    const index = selectedIndex >= 0 ? selectedIndex : 0;
    setActiveIndex(index);
    const frame = requestAnimationFrame(() => focusOption(index));
    return () => cancelAnimationFrame(frame);
    // Focus the current selection only when the list opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const selectIndex = (index: number) => {
    const opt = options[index];
    if (!opt) return;
    emitChange(onChange, String(opt.value));
    setOpen(false);
  };

  useEffect(() => {
    if (!open) return;
    const onDoc = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (event: KeyboardEvent) => {
      const inside = rootRef.current?.contains(event.target as Node) ?? false;
      if (event.key === 'Escape') {
        if (inside) setOpen(false);
        return;
      }
      if (!inside) return;
      const count = options.length;
      if (count === 0) return;
      if (
        event.key === 'ArrowDown' ||
        event.key === 'ArrowUp' ||
        event.key === 'Home' ||
        event.key === 'End'
      ) {
        event.preventDefault();
        const next =
          event.key === 'Home'
            ? 0
            : event.key === 'End'
              ? count - 1
              : event.key === 'ArrowDown'
                ? (activeIndex + 1) % count
                : (activeIndex - 1 + count) % count;
        setActiveIndex(next);
        const opt = options[next];
        if (opt) {
          document.getElementById(optionDomId(id, listId, opt))?.focus();
        }
      }
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, options, activeIndex, id, listId]);

  return (
    <div ref={rootRef} className={cn('relative', className)}>
      {name ? (
        <input type="hidden" name={name} value={value == null ? '' : String(value)} required={required} />
      ) : null}
      <button
        ref={ref}
        type="button"
        id={id}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-activedescendant={
          open && options[activeIndex]
            ? optionDomId(id, listId, options[activeIndex])
            : undefined
        }
        aria-invalid={invalid || undefined}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        onClick={() => {
          if (!disabled) setOpen((current) => !current);
        }}
        onKeyDown={(event) => {
          if (disabled) return;
          if (!open && (event.key === 'ArrowDown' || event.key === 'ArrowUp' || event.key === 'Enter' || event.key === ' ')) {
            event.preventDefault();
            setOpen(true);
            return;
          }
          if (open && (event.key === 'Enter' || event.key === ' ')) {
            event.preventDefault();
            selectIndex(activeIndex);
          }
        }}
        className={cn(
          'relative flex h-10 w-full items-center rounded-lg border bg-white pr-9 pl-3 text-left text-sm text-gray-900 shadow-xs transition-colors',
          'focus-visible:border-brand-400 focus-visible:ring-2 focus-visible:ring-brand-200 focus-visible:outline-none',
          'dark:bg-gray-900 dark:text-white dark:focus-visible:ring-brand-900/60',
          invalid
            ? 'border-red-400 focus-visible:border-red-500 focus-visible:ring-red-200 dark:border-red-500'
            : 'border-gray-300 dark:border-gray-700',
          disabled && 'cursor-not-allowed bg-gray-50 opacity-70 dark:bg-gray-800',
        )}
      >
        <span className={cn('min-w-0 flex-1 truncate', !selected && 'text-gray-400 dark:text-gray-500')}>
          {display || '\u00a0'}
        </span>
        <ChevronDown
          className="pointer-events-none absolute right-3 h-4 w-4 text-gray-400 dark:text-gray-500"
          aria-hidden="true"
        />
      </button>
      {open && !disabled ? (
        <ul
          id={listId}
          role="listbox"
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledBy ?? (id ? id : undefined)}
          className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-900"
        >
          {options.map((opt, index) => {
            const isSelected = String(opt.value) === String(value);
            const isActive = index === activeIndex;
            return (
              <li key={String(opt.value)}>
                <button
                  type="button"
                  id={optionDomId(id, listId, opt)}
                  role="option"
                  aria-selected={isSelected}
                  data-active={isActive || undefined}
                  className={cn(
                    'flex w-full px-3 py-2 text-left text-sm text-gray-900 hover:bg-gray-50 dark:text-white dark:hover:bg-gray-800',
                    isSelected && 'bg-brand-50 text-brand-700 dark:bg-brand-500/15 dark:text-brand-200',
                    isActive && !isSelected && 'bg-gray-50 dark:bg-gray-800',
                  )}
                  onClick={() => {
                    emitChange(onChange, String(opt.value));
                    setOpen(false);
                  }}
                >
                  {opt.label}
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}) as <T extends string | number = string>(
  props: SelectProps<T> & { ref?: React.Ref<HTMLButtonElement> },
) => React.ReactElement;
